import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import { Progress } from '../components/ui/progress';
import { 
  FileText, 
  Clock, 
  CheckCircle, 
  AlertCircle, 
  ArrowRight,
  Edit,
  MessageSquare,
  File,
  Calendar
} from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UserDashboard() {
  const [application, setApplication] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user, getAuthHeaders } = useAuth();
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    try {
      const [appRes, msgRes] = await Promise.all([
        axios.get(`${API_URL}/applications/mine`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/applications/mine/messages`, { headers: getAuthHeaders() })
      ]);
      setApplication(appRes.data);
      setMessages(msgRes.data || []);
    } catch (error) {
      if (error.response?.status !== 404) {
        toast.error('Failed to load application data');
      }
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const getStatusInfo = (status) => {
    switch (status) {
      case 'draft':
        return { icon: <Clock className="text-slate-400" />, text: 'Draft', color: 'bg-slate-700 text-slate-300' };
      case 'submitted':
        return { icon: <FileText className="text-violet-400" />, text: 'Under Review', color: 'bg-violet-500/20 text-violet-300' };
      case 'incomplete':
        return { icon: <AlertCircle className="text-amber-400" />, text: 'Needs Attention', color: 'bg-amber-500/20 text-amber-300' };
      case 'complete':
        return { icon: <CheckCircle className="text-emerald-400" />, text: 'Complete', color: 'bg-emerald-500/20 text-emerald-300' };
      default:
        return { icon: <Clock className="text-slate-400" />, text: 'Unknown', color: 'bg-slate-700 text-slate-300' };
    }
  };

  const getProgress = () => {
    if (!application) return 0;
    const data = application.data || {};
    const fields = ['first_name', 'last_name', 'email', 'phone', 'social_security', 'driver_license', 'work_experience', 'certifications'];
    const filled = fields.filter(f => data[f] && data[f].trim() !== '').length;
    return Math.round((filled / fields.length) * 100);
  };

  const hasFieldErrors = application?.field_errors && Object.values(application.field_errors).some(Boolean);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0F]">
        <Navbar />
        <div className="flex items-center justify-center py-32">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-violet-500"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0F]">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>
            Welcome, {user?.first_name}!
          </h1>
          <p className="text-slate-400 mt-2">
            Manage your application and track your progress
          </p>
        </div>

        {!application ? (
          /* No Application State */
          <div className="bg-[#12121A] rounded-xl p-12 text-center border border-slate-800">
            <div className="w-20 h-20 bg-violet-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <FileText className="text-violet-400" size={40} />
            </div>
            <h2 className="text-2xl font-bold text-white mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
              Start Your Application
            </h2>
            <p className="text-slate-400 max-w-md mx-auto mb-8">
              You haven't started your application yet. Begin now to join Mighty Link's team of skilled professionals.
            </p>
            <Button 
              size="lg" 
              className="btn-gradient text-white btn-hover-lift"
              onClick={() => navigate('/apply')}
              data-testid="start-application-btn"
            >
              Start Application
              <ArrowRight className="ml-2" size={18} />
            </Button>
          </div>
        ) : (
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Main Content */}
            <div className="lg:col-span-2 space-y-6">
              {/* Status Card */}
              <div className="bg-[#12121A] rounded-xl p-6 border border-slate-800" data-testid="application-status-card">
                <div className="flex items-start justify-between mb-6">
                  <div>
                    <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>
                      Application Status
                    </h2>
                    <p className="text-slate-500 text-sm mt-1">
                      Application ID: <span className="font-mono text-slate-400">{application.id?.slice(0, 8)}</span>
                    </p>
                  </div>
                  <span className={`px-4 py-2 rounded-full text-sm font-semibold ${getStatusInfo(application.status).color}`}>
                    {getStatusInfo(application.status).text}
                  </span>
                </div>

                {/* Progress Bar */}
                <div className="mb-6">
                  <div className="flex justify-between text-sm mb-2">
                    <span className="text-slate-400">Completion Progress</span>
                    <span className="font-semibold text-white">{getProgress()}%</span>
                  </div>
                  <Progress value={getProgress()} className="h-2" />
                </div>

                {/* Field Errors Alert */}
                {hasFieldErrors && (
                  <div className="bg-red-500/10 border border-red-500/30 rounded-lg p-4 mb-6" data-testid="field-errors-alert">
                    <div className="flex gap-3">
                      <AlertCircle className="text-red-400 flex-shrink-0" size={20} />
                      <div>
                        <p className="font-semibold text-red-400">Action Required</p>
                        <p className="text-red-300/80 text-sm mt-1">
                          Some fields in your application need correction. Please review and update the highlighted fields.
                        </p>
                        <ul className="mt-2 space-y-1">
                          {Object.entries(application.field_errors).filter(([_, msg]) => msg).map(([field, msg]) => (
                            <li key={field} className="text-sm text-red-300">
                              <strong className="capitalize">{field.replace(/_/g, ' ')}:</strong> {msg}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex flex-wrap gap-3">
                  <Button 
                    onClick={() => navigate('/apply')}
                    className="btn-gradient text-white"
                    data-testid="edit-application-btn"
                  >
                    <Edit size={18} className="mr-2" />
                    {application.status === 'draft' ? 'Continue Application' : 'Edit Application'}
                  </Button>
                </div>
              </div>

              {/* Application Summary */}
              <div className="bg-[#12121A] rounded-xl p-6 border border-slate-800">
                <h3 className="text-lg font-bold text-white mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
                  Application Summary
                </h3>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="p-4 bg-[#0A0A0F] rounded-lg border border-slate-700">
                    <p className="text-slate-500 text-sm">Full Name</p>
                    <p className="font-semibold text-white">
                      {application.data?.first_name} {application.data?.last_name || 'Not provided'}
                    </p>
                  </div>
                  <div className="p-4 bg-[#0A0A0F] rounded-lg border border-slate-700">
                    <p className="text-slate-500 text-sm">Email</p>
                    <p className="font-semibold text-white">{application.data?.email || 'Not provided'}</p>
                  </div>
                  <div className="p-4 bg-[#0A0A0F] rounded-lg border border-slate-700">
                    <p className="text-slate-500 text-sm">Phone</p>
                    <p className="font-semibold text-white">{application.data?.phone || 'Not provided'}</p>
                  </div>
                  <div className="p-4 bg-[#0A0A0F] rounded-lg border border-slate-700">
                    <p className="text-slate-500 text-sm">Has Own Truck</p>
                    <p className="font-semibold text-white">{application.data?.has_truck ? 'Yes' : 'No'}</p>
                  </div>
                </div>
              </div>

              {/* Documents */}
              <div className="bg-[#12121A] rounded-xl p-6 border border-slate-800">
                <h3 className="text-lg font-bold text-white mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
                  Uploaded Documents
                </h3>
                {application.documents?.length > 0 ? (
                  <div className="space-y-3">
                    {application.documents.map((doc) => (
                      <div key={doc.id} className="flex items-center gap-3 p-4 bg-[#0A0A0F] rounded-lg border border-slate-700">
                        <div className="w-10 h-10 bg-violet-500/20 rounded-lg flex items-center justify-center">
                          <File className="text-violet-400" size={20} />
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-white">{doc.filename}</p>
                          <p className="text-sm text-slate-500">{doc.document_type}</p>
                        </div>
                        <span className="text-xs text-slate-400">
                          {doc.uploaded_by === 'admin' ? 'Added by Admin' : 'Uploaded'}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-center py-8">No documents uploaded yet</p>
                )}
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Messages */}
              <div className="bg-[#12121A] rounded-xl p-6 border border-slate-800">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-bold text-white" style={{ fontFamily: 'Syne, sans-serif' }}>
                    Messages
                  </h3>
                  {messages.length > 0 && (
                    <span className="bg-violet-500/20 text-violet-300 px-2 py-1 rounded-full text-xs font-semibold">
                      {messages.length}
                    </span>
                  )}
                </div>
                
                {messages.length > 0 ? (
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {messages.slice(0, 5).map((msg) => (
                      <div 
                        key={msg.id} 
                        className={`p-3 rounded-lg ${msg.sender_role === 'admin' ? 'bg-violet-500/10 border-l-4 border-violet-500' : 'bg-[#0A0A0F]'}`}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs font-semibold text-slate-300">
                            {msg.sender_role === 'admin' ? 'Admin' : 'You'}
                          </span>
                          <span className="text-xs text-slate-500">
                            {new Date(msg.created_at).toLocaleDateString()}
                          </span>
                        </div>
                        <p className="text-sm text-slate-300">{msg.content}</p>
                        {msg.field_reference && (
                          <p className="text-xs text-violet-400 mt-1">Re: {msg.field_reference}</p>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <MessageSquare className="mx-auto text-slate-600" size={40} />
                    <p className="text-slate-500 mt-2 text-sm">No messages yet</p>
                  </div>
                )}
              </div>

              {/* Timeline */}
              <div className="bg-[#12121A] rounded-xl p-6 border border-slate-800">
                <h3 className="text-lg font-bold text-white mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
                  Timeline
                </h3>
                <div className="space-y-4">
                  <div className="flex gap-3">
                    <div className="w-8 h-8 bg-emerald-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                      <CheckCircle className="text-emerald-400" size={16} />
                    </div>
                    <div>
                      <p className="font-medium text-white text-sm">Application Created</p>
                      <p className="text-xs text-slate-500">
                        {new Date(application.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  {application.status !== 'draft' && (
                    <div className="flex gap-3">
                      <div className="w-8 h-8 bg-violet-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                        <FileText className="text-violet-400" size={16} />
                      </div>
                      <div>
                        <p className="font-medium text-white text-sm">Application Submitted</p>
                        <p className="text-xs text-slate-500">Under review by our team</p>
                      </div>
                    </div>
                  )}
                  {application.status === 'complete' && (
                    <div className="flex gap-3">
                      <div className="w-8 h-8 bg-emerald-500/20 rounded-full flex items-center justify-center flex-shrink-0">
                        <CheckCircle className="text-emerald-400" size={16} />
                      </div>
                      <div>
                        <p className="font-medium text-white text-sm">Review Complete</p>
                        <p className="text-xs text-slate-500">Your application has been approved</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      <Footer />
    </div>
  );
}
